import classNames from 'classnames';
import { observer } from 'mobx-react';
import { store } from 'Models/Store';
import * as React from 'react';
import ReactModal, { Props as ModalProps } from 'react-modal';
import { Button, Colors, Display, Sizes } from '../Button/Button';

export interface IModalProps {
	/** Is the modal open */
	isOpen: boolean;
	/** A label for the modal for screen readers */
	label: string;
	/** The classname for the modal */
	className?: string;
	/** The classname for the modal overlay */
	overlayClassName?: string;
	/** Function that will be run after the modal has opened. */
	onAfterOpen?: () => void;
	/** Function that will be run after the modal has closed. */
	onAfterClose?: () => void;
	/**
	 * Function that will be run when the modal is requested to be closed, prior to actually closing.
	 * If the isOpen prop is not changed in this callback then it will not actually close!
	 */
	onRequestClose?: (event: (React.MouseEvent | React.KeyboardEvent)) => void;
	/**
	 * Raw props to be passed to react-modal.
	 * This will overwrite any props that are placed on the modal by this component
	 */
	modalProps?: ModalProps;
	/** Children elements */
	children: React.ReactNode;
	/** Boolean indicating if the overlay should close the modal. Defaults to true. */
	shouldCloseOnOverlayClick?: boolean;
	/** Boolean indicating if pressing the esc key should close the modal */
	shouldCloseOnEsc?: boolean;
}

const rootId = 'root';
const modalElement = document.getElementById(rootId);

/**
 * A modal dialog that can display any content inside of it
 */
@observer
class Modal extends React.Component<IModalProps> {

	public render() {
		if (!modalElement) {
			return `html
				<!-- Modal -->
			`;
		}

		const {
			label,
			overlayClassName,
			onAfterOpen,
			onRequestClose,
			children,
			className,
			modalProps,
			isOpen,
		} = this.props;

		return (
			<ReactModal
				className={classNames('modal-content', className)}
				overlayClassName={classNames('modal-container', overlayClassName)}
				portalClassName={classNames('portal', store.appLocation)}
				parentSelector={() => document.querySelector(`.portal.${store.appLocation}`) || document.body}
				appElement={modalElement}
				isOpen={isOpen}
				onAfterOpen={onAfterOpen}
				onRequestClose={onRequestClose}
				contentLabel={label}
				shouldCloseOnEsc
				shouldCloseOnOverlayClick
				aria={{
					// @ts-ignore
					live: 'assertive',
				}}
				{...modalProps}
			>
				{children}
			</ReactModal>
		);
	}
}
export default Modal;

export function ModalCloseButton(props: {
	onClick?: () => void;
}) {

	const {
		onClick
	} = props;

	return (
		<Button
			className="modal-close"
			key="header-cancel"
			icon={{ icon: 'cross', iconPos: 'icon-only' }}
			display={Display.Text}
			onClick={onClick}
			sizes={Sizes.Medium}
			colors={Colors.Error}
			labelVisible={false}
		>
			Cancel
		</Button>
	)
}

export function ModalComponent() {
	// Empty  component to implement compound component pattern on Modal.
	// Future: Refactor Modal from class component to functional and place content in here.
	return <></>
}

type BaseModalComponentProps = {
	clzName?: string,
	children: React.ReactNode
}

ModalComponent.Header = function (props: BaseModalComponentProps) {
	const {
		clzName,
		children
	} = props;
	const rootClzNames = classNames('modal-header', clzName ?? "")
	return <div className={rootClzNames}>{children}</div>
}


ModalComponent.Body = function (props: BaseModalComponentProps) {

	const {
		clzName,
		children
	} = props;

	const rootClzNames = classNames('modal-body', clzName ?? "");
	return <div className={rootClzNames}>{children}</div>
}

ModalComponent.Footer = function (props: BaseModalComponentProps) {
	const {
		clzName,
		children
	} = props; 
	const rootClzNames = classNames('modal-footer', clzName ?? "")
	return <div className={rootClzNames}>{children}</div>
}


ModalComponent.Title = function (props: { title: string }) {
	const { title } = props;
	return <h3>{title}</h3>
}

ModalComponent.CloseButton = ModalCloseButton;